import React, { useEffect, useRef } from "react"

import { Home, XCircle } from "lucide-react"
import { useNavigate, useSearchParams } from "react-router-dom"

import BasicDetailsForm from "@/components/application/BasicDetailsForm"
import AppLayout from "@/components/layout/AppLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useActiveJob } from "@/contexts/ActiveJobContext"
import { useApplication } from "@/contexts/ApplicationContext"
import { NEW_APPLICATION } from "@/contexts/ApplicationContext"
import { useSession } from "@/contexts/SessionContext"
import { VISITOR_ID_KEY, useAnonymousVisitor } from "@/hooks/useAnonymousVisitor"
import { signOut } from "@/services/authService"

const BasicDetailsPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { setApplication } = useApplication()
  const { session } = useSession()
  const hasLoggedOutRef = useRef(false)
  const { createVisitor } = useAnonymousVisitor()
  const { job } = useActiveJob()

  useEffect(() => {
    // Get jobId from URL parameters
    const jobId = searchParams.get("jobId")

    if (jobId) {
      // Update application context with jobId
      setApplication((prev) => ({
        ...prev,
        jobId, // Store jobId in the application context
      }))
    }
  }, [searchParams, setApplication])

  useEffect(() => {
    if (!session || hasLoggedOutRef.current) {
      return
    }

    hasLoggedOutRef.current = true

    const jobIdFromParams = searchParams.get("jobId")
    const jobId = jobIdFromParams || ""

    const performLogout = async () => {
      console.log("Performing logout...")
      try {
        localStorage.removeItem("currentApplicationId")
        localStorage.removeItem("formCompleted")
        if (jobIdFromParams) {
          localStorage.setItem("currentJobId", jobIdFromParams)
        } else {
          localStorage.removeItem("currentJobId")
        }

        const { success, error } = await signOut()

        if (!success) {
          console.error("Error signing out existing user:", error)
        }
      } catch (error) {
        console.error("Unexpected error while signing out existing user:", error)
      } finally {
        setApplication((prev) => ({
          ...NEW_APPLICATION,
          jobId: jobId || prev.jobId || "",
        }))
        if (!jobIdFromParams) {
          navigate("/", { replace: true })
        }
      }
    }

    performLogout()
  }, [session, searchParams, setApplication, navigate])

  useEffect(() => {
    // Create visitor first if it doesn't exist, then mark form as started
    const initializeVisitorTracking = async () => {
      const visitorId = localStorage.getItem(VISITOR_ID_KEY)
      if (!visitorId) {
        const source = searchParams.get("source")
        const jobId = searchParams.get("jobId") || undefined
        // Create visitor with source information if available
        createVisitor(
          source
            ? {
                source,
                visited_at: new Date().toISOString(),
                started_form: true,
                started_form_at: new Date().toISOString(),
                ...(jobId ? { job_id: jobId } : {}),
              }
            : {
                visited_at: new Date().toISOString(),
                started_form: true,
                started_form_at: new Date().toISOString(),
                ...(jobId ? { job_id: jobId } : {}),
              },
        )
      }
    }

    initializeVisitorTracking()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check if job is inactive
  const isJobInactive = job && job.id && job.active === false

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-6">
        {isJobInactive ? (
          <div className="mx-auto max-w-lg">
            <Card>
              <CardHeader className="text-center">
                <div className="mb-4 flex justify-center">
                  <div className="rounded-full bg-amber-100 p-3">
                    <XCircle className="h-8 w-8 text-amber-600" />
                  </div>
                </div>
                <CardTitle className="text-xl font-bold text-jungle">
                  Job No Longer Active
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="mb-2 font-medium text-amber-800">
                    This position is no longer accepting applications
                  </p>
                  <p className="text-sm text-amber-700">
                    Unfortunately, this job posting has been closed and is no longer accepting new
                    applications. Please check our other available positions.
                  </p>
                </div>
              </CardContent>

              <div className="p-6 pt-0">
                <Button
                  onClick={() => navigate("/")}
                  className="w-full bg-honeycomb hover:bg-amber-500"
                >
                  <Home className="mr-2 h-4 w-4" /> View Active Jobs
                </Button>
              </div>
            </Card>
          </div>
        ) : (
          <>
            <h1 className="mb-2 text-center text-2xl font-bold text-jungle md:text-2xl">
              Basic Details
            </h1>
            <p className="mb-8 text-center text-gray-600">
              Please fill out your personal information to start your application
            </p>

            <div className="mx-auto max-w-2xl">
              <BasicDetailsForm />
            </div>
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default BasicDetailsPage
